import { createContext, useContext, useState, useCallback, useRef } from "react";

type SendMessageFn = (text: string, context?: string) => Promise<void>;

interface ChatContextValue {
    askAbout: (prompt: string, context?: string) => void;
    pendingPrompt: string | null;
    consumePendingPrompt: () => string | null;
    registerSendMessage: (fn: SendMessageFn) => void;
    openChat: () => void;
    registerOpenChat: (fn: () => void) => void;
}

const ChatContext = createContext<ChatContextValue>({
    askAbout: () => { },
    pendingPrompt: null,
    consumePendingPrompt: () => null,
    registerSendMessage: () => { },
    openChat: () => { },
    registerOpenChat: () => { },
});

export function ChatProvider({ children }: { children: React.ReactNode }) {
    const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
    const sendMessageRef = useRef<SendMessageFn | null>(null);
    const openChatRef = useRef<(() => void) | null>(null);

    const registerSendMessage = useCallback((fn: SendMessageFn) => {
        sendMessageRef.current = fn;
    }, []);

    const registerOpenChat = useCallback((fn: () => void) => {
        openChatRef.current = fn;
    }, []);

    const openChat = useCallback(() => {
        openChatRef.current?.();
    }, []);

    const consumePendingPrompt = useCallback(() => {
        const prompt = pendingPrompt;
        setPendingPrompt(null);
        return prompt;
    }, [pendingPrompt]);

    const askAbout = useCallback((prompt: string, context?: string) => {
        if (sendMessageRef.current) {
            openChatRef.current?.();
            setTimeout(() => {
                sendMessageRef.current?.(prompt, context);
            }, 400);
        } else {
            setPendingPrompt(prompt);
            openChatRef.current?.();
        }
    }, []);

    return (
        <ChatContext.Provider value={{ askAbout, pendingPrompt, consumePendingPrompt, registerSendMessage, openChat, registerOpenChat }}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChat() {
    return useContext(ChatContext);
}
